import React, { useCallback, useEffect, useMemo, useState } from "react";
import SideBar from "./SideBar";
import axiosClient from "../axios";
import { Autocomplete, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ReactComponent as Config } from "../assets/images/config.svg";
import { ReactComponent as Cross } from "../assets/images/cross.svg";
import Pagination from "./Pagination_2";
import moment from "moment";
import { ReactComponent as Edit } from "../assets/images/edit.svg";
import { ReactComponent as Add } from "../assets/images/add.svg";
import loader from "../assets/images/loader.gif";
import { useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { dateConvert } from "../dateConverter";
import "../assets/css/AdminCommon.css";
import {
  BANNER_SIZE_FILTER_OPTIONS,
  buildBannerListRequestBody,
  DISPLAY_MODE_FILTER_OPTIONS,
  isPopupBanner,
  SCREEN_POSITION_MAP,
  SCREEN_POSITIONS,
} from "../constants/bannerConfig";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DEFAULT_FILTERS = {
  page: 0,
  limit: 100,
  isActive: null,
  redirectType: "",
  isScheduled: 3,
  fullBanner: 3,
  displayMode: "",
  bannerSize: "",
  screenPosition: "",
  s_date: null,
  e_date: null,
};

function AllCustomBanner() {
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterBox, setFilterBox] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const redirectTypes = [
    { name: "All", value: "" },
    { name: "Partner", value: "partner" },
    { name: "Internal", value: "internal" },
    { name: "Video", value: "video" },
    { name: "Offer", value: "offer" },
    { name: "Custom", value: "custom" },
    { name: "None", value: "none" },
  ];

  const displayModes = DISPLAY_MODE_FILTER_OPTIONS;
  const bannerSizeFilters = BANNER_SIZE_FILTER_OPTIONS;
  const screenPositionFilters = [{ name: "All", value: "" }, ...SCREEN_POSITIONS];

  const fetchBanners = useCallback(async () => {
    setIsLoading(false);

    const bodyData = buildBannerListRequestBody(filters, searchValue);

    try {
      const res = await axiosClient.post("/admin/clickbait/all", bodyData);

      if (res?.data?.statusCode === 200) {
        setData(res?.data?.response?.totalData ?? []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(true);
    }
  }, [filters, searchValue]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchValue(value);
        setFilters((prev) => ({ ...prev, page: 0 }));
      }, 500),
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleSearchInputChange = (e) => {
    const { value } = e.target;
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    debouncedSearch.cancel();
    setSearchValue(searchInput);
    setFilters((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearSearch = () => {
    debouncedSearch.cancel();
    setSearchInput("");
    setSearchValue("");
    setFilters((prev) => ({ ...prev, page: 0 }));
  };

  const handleToggleBanner = (bannerId) => {
    axiosClient
      .post(`/admin/clickbait/toggle/${bannerId}`)
      .then((res) => {
        if (res?.data?.statusCode === 200) {
          alert(res?.data?.message);
          fetchBanners();
        }
      })
      .catch((err) => {
        console.log(err);
        alert("Error toggling banner status");
      });
  };

  const handleDeleteBanner = () => {
    setOpen(false);
    if (!deleteId) return;

    axiosClient
      .delete(`/admin/clickbait/delete/${deleteId}`)
      .then((res) => {
        if (res?.data?.statusCode === 200) {
          alert(res?.data?.message);
          fetchBanners();
        }
      })
      .catch((err) => {
        console.log(err);
        alert("Error deleting banner");
      })
      .finally(() => {
        setDeleteId(null);
      });
  };

  const handleApplyFilters = () => {
    if (dateRange.start && dateRange.end && dateRange.start > dateRange.end) {
      alert("Start date must be before end date");
      return;
    }

    setFilters({
      ...draftFilters,
      page: 0,
      s_date: dateRange.start ? dateConvert(dateRange.start) : null,
      e_date: dateRange.end ? dateConvert(dateRange.end) : null,
    });
    setFilterBox(false);
  };

  const handleResetFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setDateRange({ start: null, end: null });
    setFilters(DEFAULT_FILTERS);
  };

  const hasMorePages = data.length >= filters.limit;

  return (
    <>
      <SideBar handleSideBar={true} activeTab={"AllCustomBanner"} />

      <div className="admin-screen-v4">
        <div className="admin-header-v4">
          <div>
            <h2>All Custom Banners</h2>
          </div>
          <button
            className="btn-primary-v4"
            onClick={() => navigate("/createCustomBanner")}
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <Add />
            Add Banner
          </button>
        </div>

        <div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
          <form onSubmit={handleSearchSubmit}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--spacing-lg)", alignItems: "flex-end" }}>
              <div className="form-group-v4" style={{ flex: 1 }}>
                <label className="form-label-v4">Search Banners</label>
                <input
                  type="text"
                  className="form-input-v4"
                  placeholder="Search by title..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                />
              </div>

              <button type="submit" className="submit-button-v4">
                Search
              </button>
              {searchValue && (
                <button type="button" className="cancel-button-v4" onClick={handleClearSearch}>
                  Clear
                </button>
              )}
              <button
                type="button"
                className="btn-secondary-v4"
                onClick={() => setFilterBox(!filterBox)}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {filterBox ? <Cross /> : <Config />}
                Filters
              </button>
            </div>
          </form>

          {filterBox && (
            <div
              style={{
                marginTop: "var(--spacing-lg)",
                padding: "var(--spacing-xl)",
                background: "var(--bg-card)",
                borderRadius: "var(--radius-md)",
                border: "2px solid var(--border-color)",
              }}
            >
              <div className="offer-settings-grid-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
                <div className="form-group-v4">
                  <label className="form-label-v4">Is Active</label>
                  <div className="radio-group-v4">
                    <label className="radio-option-v4">
                      <input
                        type="radio"
                        checked={draftFilters.isActive === null || draftFilters.isActive === undefined}
                        onChange={() => setDraftFilters({ ...draftFilters, isActive: null })}
                      />
                      <span>Active</span>
                    </label>
                    <label className="radio-option-v4">
                      <input
                        type="radio"
                        checked={draftFilters.isActive === 2}
                        onChange={() => setDraftFilters({ ...draftFilters, isActive: 2 })}
                      />
                      <span>Inactive</span>
                    </label>
                    <label className="radio-option-v4">
                      <input
                        type="radio"
                        checked={draftFilters.isActive === 3}
                        onChange={() => setDraftFilters({ ...draftFilters, isActive: 3 })}
                      />
                      <span>All</span>
                    </label>
                  </div>
                </div>

                <div className="form-group-v4">
                  <label className="form-label-v4">Is Scheduled</label>
                  <div className="radio-group-v4">
                    <label className="radio-option-v4">
                      <input
                        type="radio"
                        checked={draftFilters.isScheduled === 1}
                        onChange={() => setDraftFilters({ ...draftFilters, isScheduled: 1 })}
                      />
                      <span>True</span>
                    </label>
                    <label className="radio-option-v4">
                      <input
                        type="radio"
                        checked={draftFilters.isScheduled === 2}
                        onChange={() => setDraftFilters({ ...draftFilters, isScheduled: 2 })}
                      />
                      <span>False</span>
                    </label>
                    <label className="radio-option-v4">
                      <input
                        type="radio"
                        checked={draftFilters.isScheduled === 3}
                        onChange={() => setDraftFilters({ ...draftFilters, isScheduled: 3 })}
                      />
                      <span>All</span>
                    </label>
                  </div>
                </div>

                <div className="form-group-v4">
                  <label className="form-label-v4">Redirect Type</label>
                  <div className="autocomplete-wrapper-v4">
                    <Autocomplete
                      disablePortal
                      onChange={(event, newValue) => {
                        setDraftFilters({ ...draftFilters, redirectType: newValue?.value || "" });
                      }}
                      options={redirectTypes}
                      value={
                        redirectTypes.find((option) => option.value === draftFilters.redirectType) ||
                        redirectTypes[0]
                      }
                      getOptionLabel={(option) => option.name}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select redirect type" size="small" />
                      )}
                      size="small"
                    />
                  </div>
                </div>

                <div className="form-group-v4">
                  <label className="form-label-v4">Display Mode</label>
                  <div className="autocomplete-wrapper-v4">
                    <Autocomplete
                      disablePortal
                      onChange={(event, newValue) => {
                        setDraftFilters({ ...draftFilters, displayMode: newValue?.value || "" });
                      }}
                      options={displayModes}
                      value={
                        displayModes.find((option) => option.value === draftFilters.displayMode) ||
                        displayModes[0]
                      }
                      getOptionLabel={(option) => option.name}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select display mode" size="small" />
                      )}
                      size="small"
                    />
                  </div>
                </div>

                <div className="form-group-v4">
                  <label className="form-label-v4">Banner Size</label>
                  <div className="autocomplete-wrapper-v4">
                    <Autocomplete
                      disablePortal
                      onChange={(event, newValue) => {
                        setDraftFilters({ ...draftFilters, bannerSize: newValue?.value || "" });
                      }}
                      options={bannerSizeFilters}
                      value={
                        bannerSizeFilters.find((option) => option.value === draftFilters.bannerSize) ||
                        bannerSizeFilters[0]
                      }
                      getOptionLabel={(option) => option.name}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select banner size" size="small" />
                      )}
                      size="small"
                    />
                  </div>
                </div>

                <div className="form-group-v4">
                  <label className="form-label-v4">Full Banner</label>
                  <div className="radio-group-v4">
                    <label className="radio-option-v4">
                      <input
                        type="radio"
                        checked={draftFilters.fullBanner === 1}
                        onChange={() => setDraftFilters({ ...draftFilters, fullBanner: 1 })}
                      />
                      <span>Yes</span>
                    </label>
                    <label className="radio-option-v4">
                      <input
                        type="radio"
                        checked={draftFilters.fullBanner === 2}
                        onChange={() => setDraftFilters({ ...draftFilters, fullBanner: 2 })}
                      />
                      <span>No</span>
                    </label>
                    <label className="radio-option-v4">
                      <input
                        type="radio"
                        checked={draftFilters.fullBanner === 3}
                        onChange={() => setDraftFilters({ ...draftFilters, fullBanner: 3 })}
                      />
                      <span>All</span>
                    </label>
                  </div>
                </div>

                <div className="form-group-v4">
                  <label className="form-label-v4">Screen Position</label>
                  <div className="autocomplete-wrapper-v4">
                    <Autocomplete
                      disablePortal
                      onChange={(event, newValue) => {
                        setDraftFilters({ ...draftFilters, screenPosition: newValue?.value || "" });
                      }}
                      options={screenPositionFilters}
                      value={
                        screenPositionFilters.find(
                          (option) => option.value === draftFilters.screenPosition
                        ) || screenPositionFilters[0]
                      }
                      getOptionLabel={(option) => option.name}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select screen position" size="small" />
                      )}
                      size="small"
                    />
                  </div>
                </div>

                <div className="form-group-v4">
                  <label className="form-label-v4">Created From</label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={dateRange.start}
                      onChange={(value) => setDateRange((prev) => ({ ...prev, start: value }))}
                      renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    />
                  </LocalizationProvider>
                </div>

                <div className="form-group-v4">
                  <label className="form-label-v4">Created To</label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={dateRange.end}
                      onChange={(value) => setDateRange((prev) => ({ ...prev, end: value }))}
                      renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    />
                  </LocalizationProvider>
                </div>
              </div>

              <div style={{ display: "flex", gap: "var(--spacing-md)" }}>
                <button type="button" className="submit-button-v4" onClick={handleApplyFilters}>
                  Apply Filters
                </button>
                <button type="button" className="cancel-button-v4" onClick={handleResetFilters}>
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="admin-form-container-v4">
          {isLoading ? (
            data.length > 0 ? (
              <>
                <div style={{ marginTop: "var(--spacing-xl)" }}>
                  <Pagination
                    filterDataInput={filters}
                    setFilterDataInput={setFilters}
                    showNext={hasMorePages}
                  />
                </div>
                <div className="table-container-v4">
                  <table className="data-table-v4">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Image</th>
                        <th>Name</th>
                        <th>Title</th>
                        <th>Display Mode</th>
                        <th>Size</th>
                        <th>Screen Position</th>
                        <th>Active</th>
                        <th>Priority</th>
                        <th>CTR %</th>
                        <th>Conversions</th>
                        <th>Conv. Rate</th>
                        <th>Created At</th>
                        <th>Created By</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((val, key) => {
                        const popup = isPopupBanner(val);
                        return (
                          <tr key={val._id || key}>
                            <td>{filters.page * filters.limit + (key + 1)}</td>
                            <td>
                              <img
                                src={val?.imageUrl}
                                alt="banner"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "contain",
                                  borderRadius: "var(--radius-sm)",
                                  border: "1px solid var(--border-color)",
                                }}
                              />
                            </td>
                            <td className="amount-v4">{val?.name || "-"}</td>
                            <td style={{ maxWidth: "180px" }}>
                              {popup ? (
                                <span className="text-muted-v4">— Full Screen Popup —</span>
                              ) : (
                                <>
                                  <div>{val?.title || "-"}</div>
                                  {val?.subtitle && (
                                    <div
                                      className="text-muted-v4"
                                      style={{ fontSize: "12px", marginTop: "4px" }}
                                      dangerouslySetInnerHTML={{ __html: val.subtitle }}
                                    />
                                  )}
                                </>
                              )}
                            </td>
                            <td>
                              <span className={`status-badge-v4 status-${popup ? "1" : "4"}`}>
                                {popup ? "Popup" : "Inline"}
                              </span>
                            </td>
                            <td>
                              {!popup && val?.bannerSize ? (
                                <span className="status-badge-v4 status-3">
                                  {val.bannerSize === "compact" ? "Compact" : "Standard"}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>
                              {val?.screenPosition ? (
                                <span className="text-muted-v4">
                                  {SCREEN_POSITION_MAP[val.screenPosition] || "-"}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              <span className={`status-badge-v4 status-${val?.isActive ? "1" : "2"}`}>
                                {val?.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="amount-v4">{val?.priority ?? 0}</td>
                            <td className="amount-v4">{val?.ctr ?? "0.00"}%</td>
                            <td className="text-mono-v4">{val?.conversions ?? 0}</td>
                            <td className="amount-v4">{val?.conversionRate ?? "0.00"}%</td>
                            <td className="text-mono-v4">
                              {moment.utc(val?.created_at).local().format("DD/MM/YY, h:mm a")}
                            </td>
                            <td>{val?.createdBy?.adminUsername || "-"}</td>
                            <td>
                              <div className="action-buttons-table-v4">
                                <button
                                  className="btn-action-table-v4"
                                  onClick={() => navigate(`/editCustomBanner?banner_id=${val?._id}`)}
                                  title="Edit"
                                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                                >
                                  <Edit style={{ width: "14px", height: "14px" }} />
                                  Edit
                                </button>
                                <button
                                  className="btn-action-table-v4"
                                  onClick={() => handleToggleBanner(val?._id)}
                                  title="Toggle Status"
                                >
                                  Toggle
                                </button>
                                {popup && (
                                  <button
                                    className="btn-action-table-v4"
                                    onClick={() =>
                                      navigate(`/bannerUserActivity?bannerId=${val?._id}`)
                                    }
                                    title="View Activity"
                                  >
                                    Activity
                                  </button>
                                )}
                                <button
                                  className="btn-danger-table-v4"
                                  onClick={() => {
                                    setDeleteId(val?._id);
                                    setOpen(true);
                                  }}
                                  title="Delete"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="empty-state-v4">
                <div className="empty-icon-v4">🎨</div>
                <h3>No Banners Found</h3>
                <p>Try adjusting your search or filter criteria</p>
              </div>
            )
          ) : (
            <div className="loader-container-v4">
              <img src={loader} alt="Loading..." className="loader-image-v4" />
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        className="dialogs"
      >
        <div className="dialog-content-v4">
          <h3 className="dialog-title-v4">Delete Banner</h3>
          <p className="info-text-v4">
            Are you sure you want to delete this banner? This action cannot be undone.
          </p>
          <div className="dialog-actions-v4">
            <button className="btn-primary-v4" onClick={handleDeleteBanner}>
              Yes, Delete
            </button>
            <button className="cancel-button-v4" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default AllCustomBanner;
